import React from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress } from "@material-ui/core";
import { BRAND } from "src/theme/brandTokens";

export const BOTON_BASE = {
  appearance: "none",
  cursor: "pointer",
  fontFamily: BRAND.font.family.base,
  fontSize: BRAND.font.size.eyebrow,
  fontWeight: BRAND.font.weight.bold,
  letterSpacing: BRAND.font.letterSpacing.wide,
  textTransform: "uppercase",
  py: "10pt",
  px: "16pt",
  transition:
    "background-color 150ms ease, border-color 150ms ease, color 150ms ease, opacity 150ms ease",
  display: "inline-flex",
  alignItems: "center",
  gap: "6pt",
  justifyContent: "center",
  minHeight: 36,
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.45,
  },
};

/**
 * Botón principal filled naranja. Disabled state: gris (no opacity reducida)
 * para diferenciar visualmente "deshabilitado por validación" de los demás
 * estados. Loading state opcional sustituye los `children` por un spinner
 * blanco; si el caller quiere otro shape (ej. cambiar el texto a "Enviando…"
 * sin perder el resto del contenido), debe omitir `loading` y manejar el
 * children manualmente.
 */
export const BotonPrimario = ({ onClick, disabled, loading, children, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      ...BOTON_BASE,
      bgcolor: BRAND.color.primary,
      color: BRAND.color.paper,
      border: `1.5px solid ${BRAND.color.primary}`,
      "&:hover:not(:disabled)": {
        bgcolor: BRAND.color.primaryDark,
        borderColor: BRAND.color.primaryDark,
      },
      "&:disabled": {
        cursor: "not-allowed",
        bgcolor: BRAND.color.grey30,
        borderColor: BRAND.color.grey30,
        color: BRAND.color.grey70,
        opacity: 1,
      },
      ...sx,
    }}
  >
    {loading ? (
      <CircularProgress size={18} sx={{ color: BRAND.color.paper }} />
    ) : (
      children
    )}
  </Box>
);

BotonPrimario.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

BotonPrimario.defaultProps = {
  disabled: false,
  loading: false,
  sx: undefined,
};

/**
 * Botón secundario outline gris neutro. Hover sube el borde a grey70 y agrega
 * fondo grey10 sutil. Disabled hereda el `&:disabled` del BOTON_BASE
 * (cursor + opacity 0.45).
 */
export const BotonSecundario = ({ onClick, disabled, children, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      ...BOTON_BASE,
      bgcolor: "transparent",
      color: BRAND.color.grey70,
      border: `1.5px solid ${BRAND.color.grey30}`,
      "&:hover:not(:disabled)": {
        borderColor: BRAND.color.grey70,
        bgcolor: BRAND.color.grey10,
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

BotonSecundario.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

BotonSecundario.defaultProps = {
  disabled: false,
  sx: undefined,
};
